import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import EventApi from "../../Api/EventApi";
import EventSponsorApi from "../../Api/EventSponsorApi";
import { API_URL } from "../../Api";
import { Event } from "../../Models/Event";
import { EventSponsor } from "../../Models/EventSponsor";
import Loader from "../Common/Loader";

type Section = {
  title: string;
  sponsors: EventSponsor[];
};

/** Resolve sponsor display image: sponsor image, or member logo (camelCase or PascalCase from API). Relative URLs are made absolute. */
function getSponsorImageUrl(s: EventSponsor): string | null {
  const sponsorImg = s.sponsorImageUrl ?? null;
  const raw = s.memberLogoUrl ?? (s as Record<string, unknown>).MemberLogoUrl;
  const memberLogo = typeof raw === "string" ? raw : null;
  const url = sponsorImg ?? (s.memberId ? memberLogo : null);
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const base = API_URL?.replace(/\/$/, "") ?? "";
  return base + (url.startsWith("/") ? url : "/" + url);
}

export default function EventSponsorsPage() {
  const { id } = useParams<{ id: string }>();
  const eventId = id ? parseInt(id, 10) : NaN;
  const [event, setEvent] = useState<Event | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId || isNaN(eventId)) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const [eventRes, sponsorsRes, subEventsRes] = await Promise.all([
          EventApi.getEvent(id!)
            .then((r) => r.data)
            .catch(() => null),
          EventSponsorApi.getByConferenceId(eventId)
            .then((r) => r.data)
            .catch(() => []),
          EventApi.getConferenceSubEvents(eventId)
            .then((r) => r.data)
            .catch(() => []),
        ]);
        setEvent(eventRes);

        const allSponsors: EventSponsor[] = sponsorsRes ?? [];
        const subEvents = subEventsRes ?? [];
        const sectionList: Section[] = [];

        const conferenceSponsors = allSponsors.filter((s) => s.eventId === eventId);
        if (conferenceSponsors.length > 0) {
          sectionList.push({
            title: eventRes?.name ?? "Conference",
            sponsors: conferenceSponsors,
          });
        }

        for (const sub of subEvents) {
          const subSponsors = allSponsors.filter((s) => s.eventId === sub.id);
          if (subSponsors.length > 0) {
            sectionList.push({
              title: sub.name ?? "Sub-event",
              sponsors: subSponsors,
            });
          }
        }

        setSections(sectionList);
      } catch (e) {
        console.error("[EventSponsorsPage]", e);
        setSections([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId, id]);

  if (loading) return <Loader />;

  const hasAny = sections.some((s) => s.sponsors.length > 0);
  if (!hasAny) {
    return (
      <div className="w-full p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {event?.name ?? "Event"} Sponsors
        </h1>
        <p className="text-gray-600">No sponsors at this time.</p>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {event?.name ?? "Event"} Sponsors
      </h1>
      <div className="space-y-8">
        {sections.map((section) => (
          <div key={section.title}>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
              {section.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {section.sponsors.map((s) => {
                const imageUrl = getSponsorImageUrl(s);
                return (
                  <div
                    key={s.id}
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col items-center text-center"
                  >
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={s.name}
                        className="h-20 w-auto object-contain mb-2"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded bg-gray-100 flex items-center justify-center mb-2">
                        <span className="text-2xl font-bold text-gray-400">
                          {s.name?.charAt(0) ?? "?"}
                        </span>
                      </div>
                    )}
                    <div className="font-semibold text-gray-900">{s.name}</div>
                    {s.sponsorAmount != null && s.sponsorAmount > 0 && (
                      <div className="text-sm text-gray-600 mt-1">
                        ${s.sponsorAmount.toLocaleString()}
                      </div>
                    )}
                    {s.information && (
                      <div className="text-sm text-gray-500 mt-2 line-clamp-3">
                        {s.information}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
